import { csvAmount, toCsv } from "../../creator/model";
import { uniqueNames } from "../../creator/zip";
import { t } from "../../../lib/sprache";

/**
 * Export der aktuell gefilterten Rechnungsliste (PARITAET-6, wie im Altportal AD-225/AD-226): CSV der sichtbaren Zeilen und
 * PDFs als ZIP mit Ordnern Leistungsmonat/Marke/Creator und sprechenden Dateinamen (Nummer_Marke.pdf). Für alle im Team, die
 * Rechnungen sehen; jede Person bekommt nur, was sie lesen darf (Datenbank und Speicher prüfen es).
 */

export interface ExportRow {
  number: string | null;
  kind: "talent_to_agency" | "agency_to_brand" | "talent_to_brand" | "agency_to_talent";
  issueDate: string | null;
  revenueMonth: string | null;
  customer: string;
  brand: string;
  creator: string;
  deal: string;
  status: string;
  due: string;
  currency: string;
  netCents: number | null;
  taxCents: number | null;
  grossCents: number | null;
  openCents: number;
  selfBilled?: boolean;
  creditNote?: boolean;
}

export const KIND_SHORT: Record<ExportRow["kind"], string> = {
  get agency_to_brand() { return t("Agentur an Marke", "Agency to brand"); }, get talent_to_agency() { return t("Creator an Agentur", "Creator to agency"); },
  get talent_to_brand() { return t("Creator an Marke", "Creator to brand"); }, get agency_to_talent() { return t("Agentur an Creator", "Agency to creator"); },
};

/**
 * CSV mit Semikolon und BOM (Excel); Beträge nur, wenn die Person Beträge sehen darf. Kopf und Arten in der Oberflächensprache,
 * Beträge mit ihrem Dezimalzeichen (SAAS-R3-3).
 */
export function invoiceListCsv(rows: readonly ExportRow[], withAmounts: boolean): string {
  const head = [t("Rechnung", "Invoice"), t("Art", "Type"), t("Rechnungsdatum", "Invoice date"), t("Leistungsmonat", "Service month"), t("Kunde", "Customer"),
    t("Marke", "Brand"), "Creator", t("Kooperation", "Deal"), "Status", t("Fällig", "Due"),
    ...(withAmounts ? [t("Währung", "Currency"), t("Netto", "Net"), t("Steuer", "Tax"), t("Brutto", "Gross"), t("Offen", "Open")] : [])];
  return toCsv([head, ...rows.map((r) => [
    r.number ?? "", r.selfBilled ? t("Gutschrift", "Self-billing invoice") : r.creditNote ? t("Stornorechnung", "Cancellation invoice") : KIND_SHORT[r.kind], r.issueDate ?? "", r.revenueMonth ? r.revenueMonth.slice(0, 7) : "",
    r.customer, r.brand, r.creator, r.deal, r.status, r.due,
    ...(withAmounts ? [r.currency, csvAmount(r.netCents), csvAmount(r.taxCents), csvAmount(r.grossCents), csvAmount(r.openCents)] : []),
  ])]);
}

/** Ordner- und Dateiname ohne Zeichen, die Windows, macOS oder ZIP-Programme stören */
export function safePart(s: string, fallback: string): string {
  const x = s.normalize("NFC").replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "-").replace(/\s+/g, " ").trim().replace(/^\.+/, "").slice(0, 60);
  return x || fallback;
}

/** Pfad im ZIP: Leistungsmonat (sonst Monat des Rechnungsdatums)/Marke/Creator/Nummer_Marke.pdf */
export function zipPathOf(r: Pick<ExportRow, "number" | "issueDate" | "revenueMonth" | "brand" | "creator">): string {
  const month = (r.revenueMonth ?? r.issueDate ?? "").slice(0, 7) || "ohne-Monat";
  const brand = safePart(r.brand, "ohne-Marke");
  const creator = safePart(r.creator, "ohne-Creator");
  const number = safePart(r.number ?? "", "ohne-Nummer");
  return `${month}/${brand}/${creator}/${number}_${brand}.pdf`;
}

/** Eindeutige Pfade im ZIP (gleiche Nummer und Marke zweimal: „(2)“ angehängt) */
export function zipPaths(rows: readonly Pick<ExportRow, "number" | "issueDate" | "revenueMonth" | "brand" | "creator">[]): string[] {
  return uniqueNames(rows.map(zipPathOf), { folders: true });
}
